export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (typeof init.body === "string" && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  const text = await response.text();
  let data: { error?: string; code?: string } = {};
  if (text) {
    try {
      data = JSON.parse(text) as { error?: string; code?: string };
    } catch {
      data = { error: "Réponse illisible" };
    }
  }
  if (!response.ok) {
    throw new ApiError(data.error || "Erreur inattendue", response.status, data.code);
  }
  return data as T;
}

export type User = { id: string; email: string; name: string };

export type MediaItem = {
  id: string;
  filename: string;
  originalName: string;
  type: "video" | "image";
  mimeType: string;
  size: number;
  status: "pending" | "ready";
  uploadedAt: string;
  previewUrl: string | null;
};

export type PlaylistItem = {
  id: string;
  playlistId: string;
  position: number;
  mediaId: string;
  name: string;
  type: "video" | "image";
  mimeType: string;
  size: number;
  durationSeconds: number | null;
};

export type Playlist = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  screenCount: number;
  items: PlaylistItem[];
};

export type AssignmentItem = {
  playlistId: string;
  position: number;
  mediaId: string;
  name: string;
  type: "video" | "image";
  durationSeconds: number | null;
};

export type ScreenDevice = {
  id: string;
  code: string;
  status: "pending" | "approved" | "blocked";
  ip: string | null;
  userAgent: string | null;
  agentLabel: string;
  createdAt: string;
  lastSeen: string | null;
};

export type Screen = {
  id: string;
  name: string;
  location: string | null;
  lastSeen: string | null;
  lastIp: string | null;
  playbackIp: string | null;
  status: "online" | "waiting" | "offline";
  dropped: boolean;
  refreshNonce: number;
  audioEnabled: boolean;
  createdAt: string;
  playerPath: string;
  devices: ScreenDevice[];
  assignment: {
    playlistId: string;
    playlistName: string | null;
    updatedAt: string | null;
    items: AssignmentItem[];
  } | null;
};

export function playerUrl(screen: Screen): string {
  return `${window.location.origin}${screen.playerPath}`;
}
